import React, { useState } from "react";
import { Helmet } from "react-helmet";
import { META, generateContactRichSnippet } from "./meta";
import Hero from "./Hero";

import ContactForm from "@/core/Forms/ContactForm";
import {
  BUSINESS_NAME,
  BUSINESS_CONTACT_PHONE,
  BUSINESS_CONTACT_EMAIL,
  BUSINESS_ADDRESS,
  BUSINESS_SLOGAN,
  BUSINESS_HOURS,
  BUSINESS_SOCIAL_MEDIA,
  SOCIAL_MEDIA_ICONS,
} from "@/core/constants/business";

import {
  MapPinIcon,
  PhoneIcon,
  EnvelopeIcon,
  ClockIcon,
  GlobeAltIcon,
} from "@heroicons/react/24/outline";
import {
  InstagramLogo,
  YoutubeLogo,
  FacebookLogo,
  TiktokLogo,
} from "@phosphor-icons/react";

const ContactPage: React.FC = () => {
  const richSnippet = generateContactRichSnippet();
  const [mapLoaded, setMapLoaded] = useState(false);

  return (
    <>
      <Helmet>
        <title>{META.title}</title>
        <meta name="description" content={META.meta_description} />
        <meta name="keywords" content={META.keywords} />
        <link
          rel="canonical"
          href={`https://slsdispensary.com${META.canonicalUrl}`}
        />

        {/* OpenGraph Meta Tags */}
        <meta property="og:title" content={META.title} />
        <meta property="og:description" content={META.meta_description} />
        <meta property="og:type" content="website" />
        <meta
          property="og:url"
          content={`https://slsdispensary.com${META.canonicalUrl}`}
        />
        <meta
          property="og:image"
          content="https://slsdispensary.com/images/street-lawyer-services-dc-dispensary-entrance.jpg"
        />
        <meta property="og:site_name" content={BUSINESS_NAME} />

        {/* JSON-LD for rich snippets */}
        <script type="application/ld+json">
          {JSON.stringify(richSnippet)}
        </script>
      </Helmet>

      <Hero />

      {/* Main Content */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="flex flex-col lg:flex-row gap-12">
            {/* Contact Information */}
            <div className="lg:w-1/3">
              <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
                <h2 className="text-2xl font-semibold mb-6">
                  Contact Information
                </h2>
                <div className="space-y-6 contact-details">
                  {/* Address */}
                  <div className="flex items-start">
                    <MapPinIcon className="w-6 h-6 text-cannabis-green-500 mr-3 mt-0.5" />
                    <div>
                      <h3 className="font-semibold text-lg">Address</h3>
                      <address className="not-italic">
                        {BUSINESS_ADDRESS}
                      </address>
                      <a
                        href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(
                          BUSINESS_ADDRESS
                        )}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-cannabis-green-500 hover:underline mt-1 inline-block"
                      >
                        Get Directions
                      </a>
                    </div>
                  </div>

                  {/* Phone */}
                  {/* <div className="flex items-start">
                    <PhoneIcon className="w-6 h-6 text-cannabis-green-500 mr-3 mt-0.5" />
                    <div>
                      <h3 className="font-semibold text-lg">Phone</h3>
                      <a
                        href={`tel:${BUSINESS_CONTACT_PHONE.replace(
                          /\D/g,
                          ""
                        )}`}
                        className="hover:text-cannabis-green-500"
                      >
                        {BUSINESS_CONTACT_PHONE}
                      </a>
                    </div>
                  </div> */}

                  {/* Email */}
                  {/* <div className="flex items-start">
                    <EnvelopeIcon className="w-6 h-6 text-cannabis-green-500 mr-3 mt-0.5" />
                    <div>
                      <h3 className="font-semibold text-lg">Email</h3>
                      <a
                        href={`mailto:${BUSINESS_CONTACT_EMAIL}`}
                        className="hover:text-cannabis-green-500 break-words"
                      >
                        {BUSINESS_CONTACT_EMAIL}
                      </a>
                    </div>
                  </div> */}

                  {/* Hours */}
                  <div className="flex items-start">
                    <ClockIcon className="w-6 h-6 text-cannabis-green-500 mr-3 mt-0.5" />
                    <div>
                      <h3 className="font-semibold text-lg">Hours</h3>
                      <p className="mt-1">
                        {typeof BUSINESS_HOURS.monday === "string"
                          ? `Everyday ${BUSINESS_HOURS.monday}`
                          : `Everyday ${(
                              BUSINESS_HOURS.monday as any
                            ).open?.replace(/^(\d+):00/, "$1")}am - ${(
                              BUSINESS_HOURS.monday as any
                            ).close?.replace(/^(\d+):00/, "$1")}pm`}
                      </p>
                    </div>
                  </div>
                  {/* Social Media */}
                  <div className="flex items-start">
                    <GlobeAltIcon className="w-6 h-6 text-cannabis-green-500 mr-3 mt-0.5" />
                    <div>
                      <h3 className="font-semibold text-lg">Social Media</h3>
                      <div className="flex space-x-3 mt-2">
                        <a
                          href="https://www.instagram.com/streetlawyerservices/"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-cannabis-green-500 hover:text-cannabis-green-600 transition-colors"
                          aria-label="Follow us on Instagram"
                        >
                          <InstagramLogo size={24} weight="fill" />
                        </a>
                        <a
                          href="https://www.youtube.com/@streetlawyerpodcast"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-cannabis-green-500 hover:text-cannabis-green-600 transition-colors"
                          aria-label="Subscribe to our YouTube channel"
                        >
                          <YoutubeLogo size={24} weight="fill" />
                        </a>
                        <a
                          href={BUSINESS_SOCIAL_MEDIA.facebook || "#"}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-cannabis-green-500 hover:text-cannabis-green-600 transition-colors"
                          aria-label="Visit our Facebook page"
                        >
                          <FacebookLogo size={24} weight="fill" />
                        </a>
                        <a
                          href={BUSINESS_SOCIAL_MEDIA.tiktok || "#"}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-cannabis-green-500 hover:text-cannabis-green-600 transition-colors"
                          aria-label="Follow us on TikTok"
                        >
                          <TiktokLogo size={24} weight="fill" />
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:w-2/3">
              <div className="bg-white rounded-lg shadow-lg p-8">
                <h2 className="text-2xl font-semibold mb-6">
                  Send Us a Message
                </h2>
                <ContactForm />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-12 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl font-semibold mb-6 text-center">
              Our Location
            </h2>
            <div className="rounded-lg overflow-hidden shadow-lg h-96">
              <iframe
                title="Street Lawyer Services Location"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3102.292430228946!2d-77.06085524968114!3d38.9331996064456!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x89b7c9b4587e0755%3A0x2821b96583c4bd4e!2s3400%20Connecticut%20Ave%20NW%2C%20Washington%2C%20DC%2020008!5e0!3m2!1sen!2sus!4v1650000000000!5m2!1sen!2sus"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen={true}
                loading="lazy"
                onLoad={() => setMapLoaded(true)}
              ></iframe>
            </div>
          </div>
        </div>
      </section>

      {/* Visit Us Teaser Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-2xl font-semibold mb-4">Come Visit Us Today</h2>
          <p className="text-lg mb-8 max-w-2xl mx-auto">
            The best way to learn about our products is in person. Our
            knowledgeable staff is ready to answer all your questions.
          </p>
          <div className="flex justify-center">
            <div className="inline-flex items-center bg-cannabis-green-500 hover:bg-cannabis-green-600 text-white font-medium py-3 px-8 rounded-lg transition duration-300">
              <MapPinIcon className="w-5 h-5 mr-2" />
              <a
                href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(
                  BUSINESS_ADDRESS
                )}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                Get Directions
              </a>
            </div>
          </div>
          <p className="mt-4 text-gray-600">Open Everyday - 10AM - 10PM</p>
        </div>
      </section>
    </>
  );
};

export default ContactPage;
